'use client'

import { usePathname } from 'next/navigation'
import { Loader2Icon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { BillCard } from '@/components/legislative-tracker/BillCard'
import { BillCardStack } from '@/components/legislative-tracker/BillCardStack'
import { useLegislativeContext } from '@/providers/LegislativeProvider'

/**
 * Displays bills in a stacked card grid layout.
 * Used on the main legislative tracker page (non-scoped view).
 */
export const BillFeaturedStacks = () => {
  const {
    selectedLegislativeTab,
    billTopics,
    featuredBills,
    featuredBillsLoading,
    allFilteredBills,
  } = useLegislativeContext()
  const pathname = usePathname()
  const isLegislativeTrackerPage = Boolean(pathname?.endsWith('/legislative-tracker'))

  const hasFeaturedBills = featuredBills && featuredBills.length > 0
  const isAllBillsTab = selectedLegislativeTab === 'all-bills'

  // On legislative tracker + all-bills tab: use featured bills when set, otherwise fallback to filtered scope
  const recentBills = allFilteredBills.slice(0, 6)
  const billsToDisplay =
    isLegislativeTrackerPage && isAllBillsTab
      ? hasFeaturedBills
        ? featuredBills.slice(0, 6)
        : recentBills
      : recentBills

  // Key for AnimatePresence: when this changes, previous content exits and new content enters
  const listKey =
    billsToDisplay.length > 0
      ? `${selectedLegislativeTab}-${billsToDisplay.map((b) => b.id).join(',')}`
      : `${selectedLegislativeTab}-empty`

  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={listKey}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="flex w-full flex-col flex-wrap gap-4 pb-11 md:flex-row lg:content-start [&>.bill-card]:mt-4 [&>.bill-card-stack--open]:first:mt-4"
        >
          {featuredBillsLoading ? (
            <p className="mt-6 flex w-full animate-pulse items-center justify-start gap-2 rounded-xl border border-border bg-background p-4 font-body text-sm text-muted-foreground">
              Loading bills… <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
            </p>
          ) : billsToDisplay.length > 0 ? (
            billsToDisplay.map((bill, index) => {
              if (index % 3 !== 0) return null

              const chunk = billsToDisplay.slice(index, index + 3)

              // Single bill leftover - render as standalone card
              if (chunk.length === 1) {
                return (
                  <BillCard key={bill.id} bill={bill} allBillTopics={billTopics} size="small" />
                )
              }

              // 2-3 bills - render as stack
              return (
                <BillCardStack
                  key={`${selectedLegislativeTab}-${chunk.map((b) => b.id).join('-')}`}
                  bills={chunk}
                  allBillTopics={billTopics}
                />
              )
            })
          ) : (
            <p className="mt-6 flex w-full items-center justify-start rounded-xl border border-border bg-background p-4 font-body text-sm text-muted-foreground">
              No featured bills to display
            </p>
          )}
        </motion.div>
      </AnimatePresence>
    </>
  )
}
